import * as React from "react";
import {
  FeedItemUpcomingCompo,
  FeedItemEvent,
} from "../../../server/feed/buildfeed";
import { User } from "../models/users";
import { Compo } from "../models/compos";
import { assertNever } from "../util";
import { RLink } from "../DesignSystem/RLink";
import { Button } from "../DesignSystem/Button";
import { SmallButton } from "../DesignSystem/SmallButton";
import { Countdown } from "../DesignSystem/Countdown";
import { CompoCard } from "../DesignSystem/CompoCard";
import { EventCard } from "./EventCard";
import { ShieldCheckIcon } from "@heroicons/react/24/solid";
import { TestIds } from "../testIds";

export const OngoingContests = ({
  user,
  title,
  contestsJSON,
}: {
  user?: User;
  title: string;
  contestsJSON: (FeedItemUpcomingCompo | FeedItemEvent)[];
}) => {
  const isEmpty = contestsJSON.length === 0;

  const upcomingItems = contestsJSON
    .map((item) => {
      if (item.type === "upcoming-contest") {
        const compo = new Compo(item.compo);
        let link = "";
        let text = <span></span>;
        let className = "";

        if (compo.state === "judging" && item.votesRequired) {
          link += "/judge";

          if (new Date() > compo.judging_stop) {
            text = (
              <span>
                Announcing results on{" "}
                <RLink to="https://discord.gg/AUvDuUkxXr">Discord</RLink> right
                now!
              </span>
            );
          } else {
            let voteButton = (
              <span className="inline-block">
                <SmallButton to={`/compo/${compo.id}/judge`}>Judge</SmallButton>
              </span>
            );

            const judgingStop =
              compo.judging_stop instanceof Date
                ? compo.judging_stop
                : new Date(compo.judging_stop);

            if (item.votesRequired.votes > 0) {
              if (item.votesRequired.votes === item.votesRequired.totalVotes) {
                text = (
                  <span>
                    Finished voting! Compo ends:{" "}
                    <Countdown dark minimal until={judgingStop} />
                  </span>
                );
              } else {
                text = (
                  <span>
                    {voteButton} <Countdown dark minimal until={judgingStop} />{" "}
                    left. Progress: {item.votesRequired.votes} of{" "}
                    {item.votesRequired.totalVotes}.`
                  </span>
                );
              }
            } else {
              text = (
                <span>
                  {voteButton} <Countdown dark minimal until={judgingStop} />{" "}
                  left.
                </span>
              );
            }
          }
        } else if (compo.state === "before") {
          if (compo.allow_voting) {
            text = (
              <span>
                <Countdown dark minimal until={new Date(compo.start)} /> left.{" "}
                <span className="inline-block">
                  <SmallButton
                    to={`/compo/${compo.id}/theme`}
                    className="bg-chips-gold/10 text-chips-gold hover:bg-chips-gold/20 dark:bg-chips-gold/10 dark:text-chips-gold dark:hover:bg-chips-gold/20"
                  >
                    Pick a theme!
                  </SmallButton>
                </span>
              </span>
            );
            link += "/theme";
          } else {
            text = (
              <span>
                <Countdown dark minimal until={new Date(compo.start)} /> until
                it starts.
              </span>
            );
            className = `feed-square-normal-badge`;
          }
        } else if (compo.state === "entry") {
          text = (
            <span>
              <span className="inline-block">
                <SmallButton to={`/compo/${compo.id}/submit`}>
                  Submit
                </SmallButton>
              </span>{" "}
              <Countdown dark minimal until={compo.stopDate} /> left.
            </span>
          );
          className = `feed-square-normal-badge`;
        } else if (compo.state === "over") {
          text = <span>Results are out!</span>;
          link += "/results";
        }

        return {
          type: "compo" as "compo",
          compo: compo,
          sortDate: compo.startDate,
          badge: { link, text, className },
        };
      } else {
        return {
          type: "event" as "event",
          event: item,
          sortDate: new Date(item.start),
        };
      }
    })
    .sort((a, b) => {
      const aTime =
        a.sortDate.getTime() + (a.type === "compo" ? 0 : 10000000000);
      const bTime =
        b.sortDate.getTime() + (b.type === "compo" ? 0 : 10000000000);

      return aTime - bTime;
    });

  return (
    <div
      className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-800"
      data-testid={TestIds.UpcomingSection}
    >
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {title}
      </h2>

      {isEmpty && (
        <p className="mb-3 text-sm text-slate-500 dark:text-slate-400">
          No upcoming events right now.
        </p>
      )}

      {upcomingItems.map((item) => {
        if (item.type === "compo") {
          const { compo, badge } = item;

          return <CompoCard small badge={badge} compo={compo} key={compo.id} />;
        } else if (item.type === "event") {
          return (
            <EventCard item={item.event} user={user} key={item.event.id} />
          );
        } else {
          return assertNever(item);
        }
      })}

      {user?.admin && (
        <div className="mb-3 flex gap-2">
          <Button
            to="/admin/event/new"
            variant="admin"
            icon={<ShieldCheckIcon className="h-4 w-4" />}
          >
            New event
          </Button>
          <Button
            to="/admin/compo/new"
            variant="admin"
            icon={<ShieldCheckIcon className="h-4 w-4" />}
          >
            New compo
          </Button>
        </div>
      )}

      <Button to="/compos">See All Past Compos</Button>
    </div>
  );
};
